/**
 * Agent-type vocabulary for uploaded documents.
 *
 * `enabled` is deliberately data rather than a code branch. Only the MMM
 * ingestion path is wired end to end today, so only MMM can be chosen; the rest
 * are listed and disabled so the user can see the intended surface instead of
 * wondering whether it exists. Turning one on is a one-word change here, once
 * its ingestion is actually built.
 *
 * A disabled option that silently accepted a file would be the worst outcome:
 * the document would look attached and feed nothing.
 */

import type { DocumentAgentType } from '../contracts';

export interface AgentTypeOption {
  readonly value: DocumentAgentType;
  readonly label: string;
  readonly description: string;
  /** False until that agent can genuinely consume an uploaded document. */
  readonly enabled: boolean;
}

export const AGENT_TYPE_OPTIONS: readonly AgentTypeOption[] = [
  {
    value: 'unassigned',
    label: 'Reference only',
    description: 'Stored on the bench. Not an input to any agent run.',
    enabled: true,
  },
  {
    value: 'mmm',
    label: 'MMM — Marketing Mix Modelling',
    description: 'Spend and response data for a mix model build.',
    enabled: true,
  },
  {
    value: 'data-qa',
    label: 'Data Quality Assurance — Dataset',
    description: 'Ingestion not wired yet — select a catalogued dataset instead.',
    enabled: false,
  },
  {
    value: 'dqs-measurement',
    label: 'Data Confidence Score',
    description: 'Ingestion not wired yet — select a catalogued dataset instead.',
    enabled: false,
  },
  {
    value: 'dqs-recommendation',
    label: 'Data Quality Assurance — Handover gate',
    description: 'Ingestion not wired yet — select a catalogued dataset instead.',
    enabled: false,
  },
];

const BY_VALUE = new Map(AGENT_TYPE_OPTIONS.map((option) => [option.value, option]));

export function agentTypeLabel(value: DocumentAgentType): string {
  return BY_VALUE.get(value)?.label ?? value;
}

export function isAgentTypeEnabled(value: DocumentAgentType): boolean {
  return BY_VALUE.get(value)?.enabled ?? false;
}
